import { currencyFormatter } from 'core/helpers/formatters';

// What staff see after a manual charge or refund. `unconfirmed` means we never got a result back, so
// the money may still have moved; it reads as a problem but never as "failed, try again".
export const PaymentResultStatus = {
  Succeeded: 'succeeded',
  Pending: 'pending',
  Failed: 'failed',
  Unconfirmed: 'unconfirmed'
};

const refundUnconfirmedDetail =
  "We couldn't confirm the refund went through. Refresh the page and check billing history before you try again, or contact support.";

// stripeRefundCharge: the mutation result (null when the request failed); amount in dollars.
export const refundResult = (stripeRefundCharge, amount) => {
  if (!stripeRefundCharge) {
    return { status: PaymentResultStatus.Unconfirmed, title: 'Refund not confirmed', detail: refundUnconfirmedDetail };
  }
  const { status, failureReason } = stripeRefundCharge;
  if (status === 'succeeded') {
    return {
      status: PaymentResultStatus.Succeeded,
      title: 'Refund succeeded',
      detail: `${currencyFormatter(amount)} refunded to the original payment method.`
    };
  }
  if (status === 'pending') {
    return {
      status: PaymentResultStatus.Pending,
      title: 'Refund pending',
      detail: `The ${currencyFormatter(amount)} refund hasn't settled yet. Check billing history later for its final status.`
    };
  }
  return {
    status: PaymentResultStatus.Failed,
    title: 'Refund failed',
    detail: `Stripe status: ${status}${failureReason ? `. Reason: ${failureReason}` : ''}. If the payment method is blocked or the ACH payment is more than 180 days old, refund by check.`
  };
};

// runManualCharge: the mutation result; error: what the request threw, if it threw.
export const manualChargeResult = ({ runManualCharge, amount, error }) => {
  if (error || !runManualCharge) {
    return {
      status: PaymentResultStatus.Unconfirmed,
      title: 'Payment not confirmed',
      detail: `We couldn't confirm the payment went through. Refresh the page and check billing history before you try again, or contact support.${
        error?.message ? ` Error: ${error.message}` : ''
      }`
    };
  }
  const { success, type, last4, error: chargeError } = runManualCharge;
  if (!success) {
    return {
      status: PaymentResultStatus.Failed,
      title: 'Payment failed',
      detail: chargeError?.includes('must be verified')
        ? 'Unable to run this manual charge because this ACH account has not been verified. Ask the member to add the bank account again.'
        : chargeError || 'There was an error during the manual charge.'
    };
  }
  // ACH debits take days to clear, so a successful bank charge is still pending: legacy sources report
  // `bank_account`, payment-method charges report `us_bank_account` (Stripe status `processing`).
  if (type === 'bank_account' || type === 'us_bank_account') {
    return {
      status: PaymentResultStatus.Pending,
      title: 'Payment pending',
      detail: `${currencyFormatter(amount)} from the bank account ending in ${last4} is pending. It can take 4–7 business days to show as paid.`
    };
  }
  return {
    status: PaymentResultStatus.Succeeded,
    title: 'Payment succeeded',
    detail: `${currencyFormatter(amount)} charged to the ${type} ending in ${last4}.`
  };
};
